import { useState, useCallback, useRef, useEffect } from 'react';
import { MilkdownEditor } from '@/components/MilkdownEditor';
import {
  PenLine,
  Image,
  Save,
  Send,
  Loader2,
  Hash,
  FileText,
  ChevronDown,
  X,
  Calendar,
  Check,
  ArrowLeft,
  HelpCircle
} from 'lucide-react';
import slugify from 'slugify';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/hooks/useToast';
import { useUploadFile } from '@/hooks/useUploadFile';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { cn } from '@/lib/utils';
import { DraftsPanel } from '@/components/DraftsPanel';
import { saveDraft as saveLocalDraft, deleteDraftBySlug } from '@/lib/localDrafts';
import { PublishedSuccess } from '@/components/PublishedSuccess';
import { WritingTips } from '@/components/WritingTips';
import { useDrafts } from '@/hooks/useDrafts';

interface ArticleData {
  title: string;
  summary: string;
  content: string;
  image: string;
  tags: string[];
  slug: string;
}

interface ArticleEditorProps {
  initialData?: Partial<ArticleData>;
  onPublish?: (event: unknown) => void;
  onSaveDraft?: (data: ArticleData) => void;
  onBack?: () => void;
}

export function ArticleEditor({ initialData, onPublish, onSaveDraft, onBack }: ArticleEditorProps) {
  const { user } = useCurrentUser();
  const { mutate: publishEvent, isPending: isPublishing } = useNostrPublish();
  const { mutateAsync: uploadFile, isPending: isUploading } = useUploadFile();
  const { saveDraft: saveRelayDraft, deleteDraft: deleteRelayDraft } = useDrafts();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const inlineImageInputRef = useRef<HTMLInputElement>(null);
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isMetaOpen, setIsMetaOpen] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [readingTime, setReadingTime] = useState(0);
  const [tagInput, setTagInput] = useState('');
  const [isPublished, setIsPublished] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [publishedEventId, setPublishedEventId] = useState<string | null>(null);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [originalPublishedAt, setOriginalPublishedAt] = useState<number | null>(null);

  const [article, setArticle] = useState<ArticleData>({
    title: initialData?.title || '',
    summary: initialData?.summary || '',
    content: initialData?.content || '',
    image: initialData?.image || '',
    tags: initialData?.tags || [],
    slug: initialData?.slug || '',
  });

  // Auto-save every 30 seconds if there are changes
  useEffect(() => {
    if (hasUnsavedChanges && article.content.length > 0) {
      autoSaveTimeoutRef.current = setTimeout(async () => {
        if (user) {
          try {
            await saveRelayDraft(article);
          } catch {
            // Fallback to local
            saveLocalDraft(article);
          }
        } else {
          saveLocalDraft(article);
        }
        setLastSaved(new Date());
        setHasUnsavedChanges(false);
      }, 30000);
    }

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [hasUnsavedChanges, article, user, saveRelayDraft]);

  // Reference to handlers for keyboard shortcuts
  const handlePublishRef = useRef<(() => void) | null>(null);
  const handleSaveDraftRef = useRef<(() => void) | null>(null);

  // Warn before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges && (article.title || article.content)) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, article.title, article.content]);

  // Auto-generate slug from title
  useEffect(() => {
    if (article.title && !initialData?.slug) {
      const newSlug = slugify(article.title, {
        lower: true,
        strict: true,
        trim: true
      });
      setArticle(prev => ({ ...prev, slug: newSlug }));
    }
  }, [article.title, initialData?.slug]);

  // Calculate stats
  useEffect(() => {
    const words = article.content.trim().split(/\s+/).filter(Boolean).length;
    const chars = article.content.length;
    const minutes = Math.ceil(words / 200); // Average reading speed

    setWordCount(words);
    setCharCount(chars);
    setReadingTime(minutes);
  }, [article.content]);

  const updateArticle = useCallback((field: keyof ArticleData, value: string | string[]) => {
    setArticle(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  }, []);

  const handleAddTag = useCallback(() => {
    const newTag = tagInput.trim().toLowerCase().replace(/^#/, '');
    if (newTag && !article.tags.includes(newTag)) {
      setArticle(prev => ({ ...prev, tags: [...prev.tags, newTag] }));
      setTagInput('');
    }
  }, [tagInput, article.tags]);

  const handleRemoveTag = useCallback((tagToRemove: string) => {
    setArticle(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  }, []);

  const handleImageUpload = useCallback(async (file: File) => {
    try {
      const [[, url]] = await uploadFile(file);
      return url;
    } catch (error) {
      console.error('Upload failed:', error);
      toast({
        title: 'Upload failed',
        description: 'Could not upload the image. Please try again.',
        variant: 'destructive',
      });
      return null;
    }
  }, [uploadFile]);

  const handleHeaderImageUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const url = await handleImageUpload(file);
    if (url) {
      updateArticle('image', url);
      toast({
        title: 'Image uploaded',
        description: 'Header image has been set successfully.',
      });
    }
  }, [handleImageUpload, updateArticle]);

  const handleInlineImageButtonClick = useCallback(() => {
    inlineImageInputRef.current?.click();
  }, []);

  const handleInlineImageUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const url = await handleImageUpload(file);
    if (url) {
      // Insert image markdown at cursor position
      const imageMarkdown = `![${file.name}](${url})`;
      updateArticle('content', article.content + '\n' + imageMarkdown + '\n');
    }
    // Reset input so the same file can be selected again
    e.target.value = '';
  }, [handleImageUpload, updateArticle, article.content]);

  const handleSaveDraft = useCallback(async () => {
    if (onSaveDraft) {
      onSaveDraft(article);
    }

    // Save to relay if logged in, otherwise save locally
    if (user) {
      try {
        await saveRelayDraft(article);
        setLastSaved(new Date());
        setHasUnsavedChanges(false);
        toast({
          title: 'Draft saved',
          description: 'Your article has been saved to Nostr relays.',
        });
      } catch (error) {
        console.error('Failed to save draft to relay:', error);
        // Fallback to local storage
        saveLocalDraft(article);
        setLastSaved(new Date());
        setHasUnsavedChanges(false);
        toast({
          title: 'Draft saved locally',
          description: 'Could not sync to relays. Saved to your browser.',
          variant: 'destructive',
        });
      }
    } else {
      saveLocalDraft(article);
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      toast({
        title: 'Draft saved',
        description: 'Your article has been saved locally.',
      });
    }
  }, [article, onSaveDraft, user, saveRelayDraft]);

  const handleLoadDraft = useCallback((draft: Partial<ArticleData>) => {
    setArticle(prev => ({
      ...prev,
      ...draft,
    }));
    setIsPublished(false);
    setIsEditMode(false);
    toast({
      title: 'Draft loaded',
      description: 'Your draft has been loaded into the editor.',
    });
  }, []);

  const handleLoadArticle = useCallback((articleData: Partial<ArticleData> & { publishedAt?: number }) => {
    const { publishedAt, ...data } = articleData;
    setArticle(prev => ({
      ...prev,
      ...data,
    }));
    setIsPublished(false);
    setIsEditMode(true);
    setOriginalPublishedAt(publishedAt || null);
    setHasUnsavedChanges(false);
    toast({
      title: 'Article loaded for editing',
      description: 'Make changes and publish to update your article.',
    });
  }, []);

  const handlePublish = useCallback(() => {
    if (!user) {
      toast({
        title: 'Login required',
        description: 'Please login to publish your article.',
        variant: 'destructive',
      });
      return;
    }

    if (!article.title.trim()) {
      toast({
        title: 'Title required',
        description: 'Please add a title to your article.',
        variant: 'destructive',
      });
      return;
    }

    if (!article.content.trim()) {
      toast({
        title: 'Content required',
        description: 'Please write some content for your article.',
        variant: 'destructive',
      });
      return;
    }

    // Use original published_at when editing, current time for new articles
    const publishedAtTimestamp = isEditMode && originalPublishedAt
      ? Math.floor(originalPublishedAt / 1000)
      : Math.floor(Date.now() / 1000);

    const tags: string[][] = [
      ['d', article.slug || slugify(article.title, { lower: true, strict: true })],
      ['title', article.title],
      ['published_at', publishedAtTimestamp.toString()],
    ];

    if (article.summary) {
      tags.push(['summary', article.summary]);
    }

    if (article.image) {
      tags.push(['image', article.image]);
    }

    article.tags.forEach(tag => {
      tags.push(['t', tag]);
    });

    publishEvent(
      {
        kind: 30023,
        content: article.content,
        tags,
      },
      {
        onSuccess: async (event) => {
          setIsPublished(true);
          setHasUnsavedChanges(false);
          setPublishedEventId(event.id);
          setShowSuccessDialog(true);

          // Remove draft after publishing (both local and relay)
          if (article.slug) {
            deleteDraftBySlug(article.slug);
            // Also delete from relay
            try {
              await deleteRelayDraft(article.slug);
            } catch (error) {
              console.error('Failed to delete draft from relay:', error);
            }
          }

          if (onPublish) {
            onPublish(event);
          }
        },
        onError: (error) => {
          toast({
            title: 'Publishing failed',
            description: error.message || 'Could not publish your article. Please try again.',
            variant: 'destructive',
          });
        },
      }
    );
  }, [user, article, publishEvent, onPublish, deleteRelayDraft, isEditMode, originalPublishedAt]);

  // Set refs for keyboard shortcuts
  handlePublishRef.current = handlePublish;
  handleSaveDraftRef.current = handleSaveDraft;

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + S to save
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        handleSaveDraftRef.current?.();
      }
      // Cmd/Ctrl + Enter to publish (if logged in)
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && user) {
        e.preventDefault();
        handlePublishRef.current?.();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [user]);

  const handleWriteAnother = useCallback(() => {
    setArticle({
      title: '',
      summary: '',
      content: '',
      image: '',
      tags: [],
      slug: '',
    });
    setIsPublished(false);
    setIsEditMode(false);
    setOriginalPublishedAt(null);
    setPublishedEventId(null);
    setLastSaved(null);
    setHasUnsavedChanges(false);
  }, []);

  // Handle back navigation with unsaved changes check
  const handleBack = useCallback(() => {
    if (hasUnsavedChanges && (article.title || article.content)) {
      setShowLeaveDialog(true);
    } else {
      onBack?.();
    }
  }, [hasUnsavedChanges, article.title, article.content, onBack]);

  const handleLeaveWithoutSaving = useCallback(() => {
    setShowLeaveDialog(false);
    onBack?.();
  }, [onBack]);

  const handleSaveAndLeave = useCallback(async () => {
    await handleSaveDraft();
    setShowLeaveDialog(false);
    onBack?.();
  }, [handleSaveDraft, onBack]);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header Bar */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {onBack && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBack}
                className="text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            )}
            <div className="flex items-center gap-2 text-primary">
              <PenLine className="w-5 h-5" />
              <span className="font-serif text-lg font-semibold hidden sm:inline">Inkwell</span>
            </div>
            <span className="text-muted-foreground text-sm hidden sm:flex items-center gap-2">
              {isPublished ? (
                <span className="text-green-600 dark:text-green-400">
                  {isEditMode ? 'Updated' : 'Published'}
                </span>
              ) : isEditMode ? (
                hasUnsavedChanges ? (
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                    Editing
                  </span>
                ) : (
                  <span className="text-blue-600 dark:text-blue-400">Editing</span>
                )
              ) : hasUnsavedChanges ? (
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                  Unsaved
                </span>
              ) : lastSaved ? (
                <span>Saved</span>
              ) : (
                <span>Draft</span>
              )}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <DraftsPanel onSelectDraft={handleLoadDraft} onSelectArticle={handleLoadArticle} />

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSaveDraft}
                  className="text-muted-foreground"
                >
                  <Save className="w-4 h-4" />
                  <span className="hidden sm:inline ml-1.5">Save</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Save Draft</TooltipContent>
            </Tooltip>

            <Button
              onClick={handlePublish}
              disabled={isPublishing || !user}
              className="gap-2"
            >
              {isPublishing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="hidden sm:inline">{isEditMode ? 'Updating...' : 'Publishing...'}</span>
                </>
              ) : isPublished ? (
                <>
                  <Check className="w-4 h-4" />
                  <span className="hidden sm:inline">{isEditMode ? 'Updated' : 'Published'}</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span className="hidden sm:inline">{isEditMode ? 'Update' : 'Publish'}</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-8 animate-fade-in">
          {/* Header Image Area */}
          {article.image ? (
            <div className="relative mb-8 rounded-xl overflow-hidden group">
              <img
                src={article.image}
                alt="Header"
                className="w-full h-64 object-cover"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Image className="w-4 h-4 mr-2" />
                  )}
                  Change Image
                </Button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="w-full mb-8 h-40 border-2 border-dashed border-border rounded-xl flex flex-col items-center justify-center text-muted-foreground hover:border-primary/50 hover:text-primary/70 transition-colors"
            >
              {isUploading ? (
                <Loader2 className="w-8 h-8 animate-spin mb-2" />
              ) : (
                <Image className="w-8 h-8 mb-2" />
              )}
              <span className="text-sm">Add a header image</span>
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleHeaderImageUpload}
            className="hidden"
          />
          <input
            ref={inlineImageInputRef}
            type="file"
            accept="image/*"
            onChange={handleInlineImageUpload}
            className="hidden"
          />

          {/* Title Input */}
          <input
            type="text"
            value={article.title}
            onChange={(e) => updateArticle('title', e.target.value)}
            placeholder="Your article title..."
            className="w-full text-4xl sm:text-5xl font-serif font-bold bg-transparent border-none outline-none placeholder:text-muted-foreground/40 mb-4"
          />

          {/* Meta Section */}
          <Collapsible open={isMetaOpen} onOpenChange={setIsMetaOpen} className="mb-6">
            <CollapsibleTrigger asChild>
              <button className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <FileText className="w-4 h-4" />
                <span>Article details</span>
                <ChevronDown className={cn(
                  "w-4 h-4 transition-transform",
                  isMetaOpen && "rotate-180"
                )} />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-4 space-y-4 animate-fade-in">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="slug" className="text-muted-foreground">URL Slug</Label>
                  <Input
                    id="slug"
                    value={article.slug}
                    onChange={(e) => updateArticle('slug', e.target.value)}
                    placeholder="article-url-slug"
                    className="font-mono text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground flex items-center gap-2">
                    <Hash className="w-3.5 h-3.5" />
                    Tags
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                      placeholder="Add a tag..."
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={handleAddTag}
                    >
                      Add
                    </Button>
                  </div>
                </div>
              </div>

              {article.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {article.tags.map(tag => (
                    <Badge
                      key={tag}
                      variant="secondary"
                      className="gap-1 px-2 py-1"
                    >
                      #{tag}
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="summary" className="text-muted-foreground">Summary</Label>
                <Textarea
                  id="summary"
                  value={article.summary}
                  onChange={(e) => updateArticle('summary', e.target.value)}
                  placeholder="A brief description of your article..."
                  rows={3}
                  className="resize-none"
                />
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Markdown Help */}
          <div className="flex items-center justify-end gap-1 mb-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground">
                  <HelpCircle className="w-3.5 h-3.5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72" align="end">
                <div className="space-y-2">
                  <h4 className="font-medium text-sm">Markdown Quick Reference</h4>
                  <div className="text-xs space-y-1.5 font-mono text-muted-foreground">
                    <div className="flex justify-between"><span>**bold**</span><span className="font-sans font-bold">bold</span></div>
                    <div className="flex justify-between"><span>*italic*</span><span className="font-sans italic">italic</span></div>
                    <div className="flex justify-between"><span># Heading 1</span><span className="font-sans">H1</span></div>
                    <div className="flex justify-between"><span>## Heading 2</span><span className="font-sans">H2</span></div>
                    <div className="flex justify-between"><span>- list item</span><span className="font-sans">• item</span></div>
                    <div className="flex justify-between"><span>1. numbered</span><span className="font-sans">1. item</span></div>
                    <div className="flex justify-between"><span>[text](url)</span><span className="font-sans text-primary">link</span></div>
                    <div className="flex justify-between"><span>![alt](url)</span><span className="font-sans">image</span></div>
                    <div className="flex justify-between"><span>&gt; quote</span><span className="font-sans border-l-2 pl-1">quote</span></div>
                    <div className="flex justify-between"><span>`code`</span><span className="font-sans bg-muted px-1 rounded">code</span></div>
                  </div>
                  <p className="text-xs text-muted-foreground pt-2 border-t">
                    Drag & drop or paste images to upload
                  </p>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Editor */}
          <MilkdownEditor
            value={article.content}
            onChange={(value) => updateArticle('content', value || '')}
            onUploadImage={handleImageUpload}
            onImageButtonClick={handleInlineImageButtonClick}
            placeholder="Start writing your article..."
            className="rounded-xl overflow-hidden border border-border bg-card min-h-[400px]"
          />

          {/* Stats Bar */}
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <FileText className="w-4 h-4" />
              <span>{wordCount} words</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span>{charCount} characters</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              <span>{readingTime} min read</span>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-background/50 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Vibed with{' '}
            <a
              href="https://shakespeare.diy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Shakespeare
            </a>
          </span>
          <span className="hidden sm:block">
            Powered by Nostr · Kind 30023
          </span>
        </div>
      </footer>

      {/* Writing Tips */}
      <WritingTips />

      {/* Published Success Dialog */}
      <PublishedSuccess
        open={showSuccessDialog}
        onOpenChange={setShowSuccessDialog}
        eventId={publishedEventId || undefined}
        pubkey={user?.pubkey}
        dTag={article.slug}
        title={article.title}
        onWriteAnother={handleWriteAnother}
      />

      {/* Leave Confirmation Dialog */}
      <AlertDialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Would you like to save your draft before leaving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={() => setShowLeaveDialog(false)}>
              Cancel
            </AlertDialogCancel>
            <Button variant="outline" onClick={handleLeaveWithoutSaving}>
              Discard
            </Button>
            <AlertDialogAction onClick={handleSaveAndLeave}>
              Save Draft
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
